import { useTranslation } from "react-i18next";

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center text-white px-8 overflow-hidden"
    >
      {/* Content */}
      <div className="relative z-10 max-w-5xl text-center">
        {/* Hexagon Logo with DNA Symbol - Neuro Glow */}
        <div className="mb-12 animate-fade-in relative inline-block">
          {/* Outer glow effect */}
          <div
            className="absolute bg-neuro-cyan/40 blur-3xl opacity-60 animate-pulse-glow"
            style={{
              width: "200px",
              height: "230px",
              top: "-15px",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          ></div>

          <div
            onClick={() => {
              const element = document.getElementById("about");
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
              }
            }}
            className="hexagon-large relative bg-gradient-to-br from-neuro-cyan/30 to-neuro-purple/20 glass-neuro mx-auto animate-honeycomb border-2 border-neuro-cyan/40 group cursor-pointer hover:scale-110 transition-all duration-500"
            style={{
              width: "160px",
              height: "184px",
              boxShadow:
                "0 0 30px rgba(0, 245, 212, 0.4), inset 0 0 30px rgba(0, 245, 212, 0.1)",
            }}
          >
            {/* Inner shadow for depth */}
            <div
              className="absolute inset-0 hexagon-large opacity-50"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0, 245, 212, 0.3) 0%, transparent 50%, rgba(0, 0, 0, 0.4) 100%)",
              }}
            ></div>

            <div className="absolute inset-0 flex items-center justify-center animate-dna">
              <svg
                className="w-28 h-28 text-neuro-cyan group-hover:text-white transition-colors duration-500"
                viewBox="0 0 100 100"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  filter: "drop-shadow(0 0 12px rgba(0, 245, 212, 0.8))",
                }}
              >
                {/* Left DNA Strand - Smooth Helix */}
                <path
                  d="M 25 10 Q 20 20 25 30 T 25 50 T 25 70 T 25 90"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  className="opacity-90"
                />

                {/* Right DNA Strand - Smooth Helix */}
                <path
                  d="M 75 10 Q 80 20 75 30 T 75 50 T 75 70 T 75 90"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  className="opacity-90"
                />

                {/* Base Pairs - Horizontal Connections with gradient effect */}
                <g className="opacity-70">
                  <line
                    x1="25"
                    y1="15"
                    x2="75"
                    y2="15"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="23"
                    x2="75"
                    y2="23"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="31"
                    x2="75"
                    y2="31"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="39"
                    x2="75"
                    y2="39"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="47"
                    x2="75"
                    y2="47"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="55"
                    x2="75"
                    y2="55"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="63"
                    x2="75"
                    y2="63"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="71"
                    x2="75"
                    y2="71"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="79"
                    x2="75"
                    y2="79"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="25"
                    y1="87"
                    x2="75"
                    y2="87"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </g>

                {/* Nucleotide Nodes - Left Strand (Adenine & Guanine) */}
                <circle
                  cx="25"
                  cy="15"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0s" }}
                />
                <circle
                  cx="25"
                  cy="31"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.2s" }}
                />
                <circle
                  cx="25"
                  cy="47"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.4s" }}
                />
                <circle
                  cx="25"
                  cy="63"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.6s" }}
                />
                <circle
                  cx="25"
                  cy="79"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.8s" }}
                />

                {/* Nucleotide Nodes - Right Strand (Thymine & Cytosine) */}
                <circle
                  cx="75"
                  cy="15"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.1s" }}
                  opacity="0.9"
                />
                <circle
                  cx="75"
                  cy="31"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.3s" }}
                  opacity="0.9"
                />
                <circle
                  cx="75"
                  cy="47"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.5s" }}
                  opacity="0.9"
                />
                <circle
                  cx="75"
                  cy="63"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.7s" }}
                  opacity="0.9"
                />
                <circle
                  cx="75"
                  cy="79"
                  r="3"
                  fill="currentColor"
                  className="animate-pulse"
                  style={{ animationDelay: "0.9s" }}
                  opacity="0.9"
                />

                {/* Middle Connection Points for depth */}
                <g className="opacity-50">
                  <circle cx="50" cy="15" r="1.5" fill="currentColor" />
                  <circle cx="50" cy="31" r="1.5" fill="currentColor" />
                  <circle cx="50" cy="47" r="1.5" fill="currentColor" />
                  <circle cx="50" cy="63" r="1.5" fill="currentColor" />
                  <circle cx="50" cy="79" r="1.5" fill="currentColor" />
                </g>

                {/* Outer Glow Rings for emphasis */}
                <g className="opacity-30">
                  <circle cx="25" cy="15" r="5" fill="none" strokeWidth="0.5" />
                  <circle cx="25" cy="47" r="5" fill="none" strokeWidth="0.5" />
                  <circle cx="25" cy="79" r="5" fill="none" strokeWidth="0.5" />
                  <circle cx="75" cy="31" r="5" fill="none" strokeWidth="0.5" />
                  <circle cx="75" cy="63" r="5" fill="none" strokeWidth="0.5" />
                </g>
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-6xl md:text-8xl font-bold mb-6 animate-slide-up gradient-text text-neuro-glow">
          {t("hero.title")}
        </h1>

        <p className="text-2xl md:text-4xl mb-4 text-neuro-cyan/90 font-light animate-slide-up animation-delay-100">
          {t("hero.subtitle")}
        </p>

        <p className="text-lg md:text-xl mb-12 text-gray-300 max-w-3xl mx-auto animate-slide-up animation-delay-200">
          {t("hero.description")}
        </p>

        <div className="flex gap-6 justify-center flex-wrap animate-slide-up animation-delay-300">
          <button
            onClick={() => {
              const element = document.getElementById("contact");
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
              }
            }}
            className="group relative bg-neuro-cyan text-dark-950 px-10 py-4 rounded-full font-semibold text-lg overflow-hidden transition-all duration-300 hover:scale-105 neuro-glow-cyan cursor-pointer"
          >
            <span className="relative z-10 group-hover:text-white transition-colors">
              {t("hero.cta.start")}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-neuro-purple to-neuro-azure opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>

          <button
            onClick={() => {
              const element = document.getElementById("features");
              if (element) {
                element.scrollIntoView({ behavior: "smooth" });
              }
            }}
            className="group glass-neuro border-2 border-neuro-cyan/30 px-10 py-4 rounded-full font-semibold text-lg hover:bg-neuro-cyan/10 hover:border-neuro-cyan/50 hover:scale-105 transition-all duration-300 neuro-glow-cyan cursor-pointer"
          >
            {t("hero.cta.learn")}
            <span className="inline-block ml-2 group-hover:translate-x-2 transition-transform">
              →
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
